import { useState, useEffect, useCallback, useRef } from "react";
import {
  getNicheAds, updateNicheAdsConfig, runNicheAds, getNicheAdsRun,
  geocodeAddress, citiesInRadius, dfsBalance, rankCountries,
  type NicheAdsConfig, type NicheAdsCity, type NicheAdsRun, type NicheAdsRunMeta,
  type NicheAdsResult, type DfsBalance, type RankCountry,
} from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { showAlert, showConfirm } from "../lib/dialog";
import { CityPicker, prettyLoc } from "./CityPicker";

// Niche Ads Scraper — runs a DataForSEO keyword×city×device grid and compiles the domains running Google
// ads (regular paid text ads, Local Services Ads, and paid Maps/local-pack entries) with the keywords
// they target and the cities they show in. Works for ANY industry — the user supplies the keywords.
// DataForSEO-only (no WordPress). Runs as a background job the client polls for progress; a schedule can
// re-run it at chosen times of day.

interface Props { siteUrl?: string; siteBrand?: string; refreshToken?: number }

const PER_SEARCH_USD = 0.02; // approx per request: Bright Data screenshot + Claude vision read
const LOW_BALANCE_USD = 10;
const MAX_SEARCHES = 400;
const DEFAULT_COUNTRY = "United States";
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIME_PRESETS: [string, string][] = [["Morning", "09:00"], ["Afternoon", "14:00"], ["Evening", "18:00"]];
const SURFACES: [string, string][] = [["paid", "Search ads"], ["localservices", "Local Services"], ["localpack", "Map pack"], ["shopping", "Shopping"]];
const AD_TYPE_LABEL: Record<string, string> = { search: "Search Ad", localservices: "Local Services", map: "Map Pack", shopping: "Shopping", paid: "Ad", lsa: "Local Services", localpack: "Map" };
// Google Ads Transparency Center region code by country (defaults to US). The Center is searchable by
// domain directly: https://adstransparency.google.com/?region=US&domain=example.com — no API needed.
const REGION_BY_COUNTRY: Record<string, string> = {
  "United States": "US", "United Kingdom": "GB", Canada: "CA", Australia: "AU", Ireland: "IE",
  "New Zealand": "NZ", Germany: "DE", France: "FR", Spain: "ES", Italy: "IT", Netherlands: "NL",
  India: "IN", Mexico: "MX", Brazil: "BR",
};
const transparencyUrl = (domain: string, country: string) =>
  `https://adstransparency.google.com/?region=${REGION_BY_COUNTRY[country] || "US"}&domain=${encodeURIComponent(domain)}`;
// Which device(s) the domain's ads were seen on.
const deviceLabel = (devices: string[] | undefined): string => {
  const arr = devices || [];
  const d = arr.includes("desktop"), m = arr.includes("mobile");
  return d && m ? "Both" : d ? "Desktop" : m ? "Mobile" : "—";
};

const usd = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtDateTime = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? iso.slice(0, 16) : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };
const localTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })();
const dedupeCities = (list: NicheAdsCity[]): NicheAdsCity[] => { const seen = new Set<string>(); const out: NicheAdsCity[] = []; for (const c of list) { const k = c.label.toLowerCase(); if (c.label && !seen.has(k)) { seen.add(k); out.push(c); } } return out; };

type Loc = { name: string; code: number } | null;
const isTerminal = (s: string) => s === "done" || s === "error" || s === "interrupted";

export function NicheAdsScraper(props: Props) {
  const key = props.siteUrl ? siteKey(props.siteUrl) : "";
  const notReady = !key;

  const [keywordsText, setKeywordsText] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countries, setCountries] = useState<RankCountry[]>([]);
  const [cities, setCities] = useState<NicheAdsCity[]>([]);
  const [cityPick, setCityPick] = useState<Loc>(null);
  const [radiusCenter, setRadiusCenter] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [radiusBusy, setRadiusBusy] = useState(false);
  const [devices, setDevices] = useState<string[]>(["desktop"]);
  const [surfaces, setSurfaces] = useState<string[]>(["paid", "localservices", "localpack", "shopping"]);
  const [pages, setPages] = useState(1);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedDays, setSchedDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [schedTimes, setSchedTimes] = useState<string[]>(["09:00", "14:00"]);
  const [customTime, setCustomTime] = useState("");
  const [schedTz, setSchedTz] = useState(localTz);
  const [nextRunAt, setNextRunAt] = useState<string | null>(null);

  const [balance, setBalance] = useState<DfsBalance | null>(null);
  const [runs, setRuns] = useState<NicheAdsRunMeta[]>([]);
  const [run, setRun] = useState<NicheAdsRun | null>(null);
  const [selectedRun, setSelectedRun] = useState<string>("latest");
  const [running, setRunning] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);
  const [sortKey, setSortKey] = useState<"appearances" | "keywords" | "cities">("appearances");


  const pollRef = useRef<number | null>(null);
  const keywords = keywordsText.split("\n").map(k => k.trim()).filter(Boolean);
  const searches = cities.length * keywords.length * (devices.length || 1);
  const overCap = searches > MAX_SEARCHES;
  const requests = searches * Math.max(1, pages); // Bright Data calls = searches × pages scanned

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const poll = useCallback((runId: string) => {
    stopPoll();
    setRunning(true);
    const tick = async () => {
      try {
        const r = await getNicheAdsRun(runId);
        if (!r) return;
        setRun(r);
        if (isTerminal(r.status)) {
          stopPoll(); setRunning(false);
          setStatus(r.status === "done" ? { tone: "info", text: `Done — ${r.result?.domains.length ?? 0} advertiser domain(s) found.` }
            : r.status === "error" ? { tone: "err", text: r.error || "Scrape failed." }
            : { tone: "err", text: "The run was interrupted (server restart). Run it again." });
        } else {
          setStatus({ tone: "info", text: `Scanning… ${r.done}/${r.total} searches, ${r.result?.domains.length ?? 0} domains so far.` });
        }
      } catch { /* transient poll error — keep polling */ }
    };
    void tick();
    pollRef.current = window.setInterval(() => void tick(), 4000);
  }, []);

  const applyConfig = useCallback((c: NicheAdsConfig | null) => {
    if (!c) return;
    setKeywordsText(c.keywords.join("\n"));
    setCountry(c.country || DEFAULT_COUNTRY);
    setCities(c.cities);
    if (c.devices.length) setDevices(c.devices);
    if (c.surfaces.length) setSurfaces(c.surfaces);
    setPages(c.pages || 1);
    setSchedEnabled(c.scheduleEnabled);
    if (c.scheduleDays.length) setSchedDays(c.scheduleDays);
    if (c.scheduleTimes.length) setSchedTimes(c.scheduleTimes);
    if (c.scheduleTz) setSchedTz(c.scheduleTz);
    setNextRunAt(c.nextRunAt);
  }, []);

  const load = useCallback(async () => {
    if (!key) return;
    try {
      const d = await getNicheAds(key);
      applyConfig(d.config);
      setRuns(d.runs); setRun(d.latest); setSelectedRun("latest"); setLoaded(true);
      if (d.latest && !isTerminal(d.latest.status)) poll(d.latest.id);
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load." }); }
  }, [key, applyConfig, poll]);

  useEffect(() => { setRun(null); setRuns([]); setLoaded(false); setStatus(null); stopPoll(); setRunning(false); }, [key]);
  useEffect(() => { void load(); return stopPoll; }, [load, props.refreshToken]);
  useEffect(() => { (async () => { try { setBalance(await dfsBalance()); } catch { /* non-fatal */ } })(); }, []);
  useEffect(() => { let alive = true; (async () => { try { const c = await rankCountries(); if (alive) setCountries(c); } catch { /* default */ } })(); return () => { alive = false; }; }, []);

  const persist = useCallback(async () => {
    if (!key) return;
    try {
      const c = await updateNicheAdsConfig(key, {
        keywords, cities, country, devices, surfaces, pages,
        scheduleEnabled: schedEnabled, scheduleDays: schedDays, scheduleTimes: schedTimes, scheduleTz: schedTz,
      });
      setNextRunAt(c.nextRunAt);
    } catch { /* soft-fail */ }
  }, [key, keywords, cities, country, devices, surfaces, pages, schedEnabled, schedDays, schedTimes, schedTz]);

  const addCity = () => { if (cityPick) { setCities(prev => dedupeCities([...prev, { label: prettyLoc(cityPick.name), locationCode: cityPick.code }])); setCityPick(null); setTimeout(() => void persist(), 0); } };
  const removeCity = (i: number) => { setCities(prev => prev.filter((_, j) => j !== i)); setTimeout(() => void persist(), 0); };
  const populateRadius = async () => {
    if (!radiusCenter.trim() || radiusBusy) return;
    setRadiusBusy(true);
    try {
      const geo = await geocodeAddress(radiusCenter.trim());
      if (!geo) { void showAlert("Couldn't find that location. Try 'City, ST'.", { title: "Not Found" }); return; }
      const found = await citiesInRadius(geo.lat, geo.lng, radiusMiles);
      if (!found.length) { void showAlert("No cities found in that radius.", { title: "No Cities" }); return; }
      setCities(prev => dedupeCities([...prev, ...found.map(c => ({ label: `${c.city}, ${c.state}`, lat: c.lat, lng: c.lng }))]));
      setTimeout(() => void persist(), 0);
    } catch (e) { void showAlert(e instanceof Error ? e.message : "Radius lookup failed.", { title: "Error" }); }
    finally { setRadiusBusy(false); }
  };

  const toggle = (arr: string[], v: string, set: (a: string[]) => void) => { set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]); setTimeout(() => void persist(), 0); };
  const toggleDay = (d: number) => { setSchedDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort()); setTimeout(() => void persist(), 0); };
  const toggleTime = (t: string) => { setSchedTimes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort()); setTimeout(() => void persist(), 0); };
  const addCustomTime = () => { if (/^\d{1,2}:\d{2}$/.test(customTime) && !schedTimes.includes(customTime)) { setSchedTimes(prev => [...prev, customTime].sort()); setCustomTime(""); setTimeout(() => void persist(), 0); } };

  const doRun = useCallback(async () => {
    if (running || notReady) return;
    if (!keywords.length) { void showAlert("Add at least one keyword (one per line).", { title: "Keywords Required" }); return; }
    if (!cities.length) { void showAlert("Add at least one city (bulk or by radius).", { title: "Cities Required" }); return; }
    if (overCap) { void showAlert(`That grid is ${searches} searches, over the ${MAX_SEARCHES} cap. Reduce keywords, cities, or devices.`, { title: "Grid Too Large" }); return; }
    const ok = await showConfirm(
      `This runs ${searches} searches (${cities.length} cities × ${keywords.length} keywords × ${devices.length} device${devices.length > 1 ? "s" : ""})${pages > 1 ? ` across ${pages} pages = ${requests} requests` : ""} at about ${usd(requests * PER_SEARCH_USD)}. Continue?`,
      { title: "Run Niche Ads Scrape", confirmLabel: "Run", cancelLabel: "Cancel", tone: "info" },
    );
    if (!ok) return;
    await persist();
    setRunning(true); setStatus({ tone: "info", text: "Starting…" });
    try { const { runId } = await runNicheAds(key); setSelectedRun("latest"); poll(runId); }
    catch (e) { setRunning(false); setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not start." }); }
  }, [running, notReady, keywords, cities, devices, searches, overCap, persist, key, poll]);

  const onSelectRun = useCallback(async (id: string) => {
    setSelectedRun(id);
    if (id === "latest") { void load(); return; }
    stopPoll(); setRunning(false);
    try { const r = await getNicheAdsRun(id); if (r) { setRun(r); if (!isTerminal(r.status)) poll(r.id); } }
    catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load run." }); }
  }, [load, poll]);

  // ── Theme ──
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const cardBg = isDark ? "#16223a" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const boxBg = isDark ? "rgba(255,255,255,.04)" : "#f8fafc";
  const boxBorder = isDark ? "rgba(255,255,255,.12)" : "#e2e8f0";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const inkMid = isDark ? "#cbd5e1" : "#334155";
  const link = isDark ? "#7dd3fc" : "#2563eb";
  const red = isDark ? "#f87171" : "#dc2626";
  const green = isDark ? "#86efac" : "#16a34a";
  const accent = "#f97316";
  const chipBg = isDark ? "rgba(255,255,255,.06)" : "#f1f5f9";
  const inp: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "7px 10px", border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, borderRadius: 6, fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", background: isDark ? "#1a2740" : "#fff", color: ink, outline: "none" };
  const lbl: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3 };
  const help: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 820 };
  const brandTitle: React.CSSProperties = { fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: accent, letterSpacing: ".7px" };
  const bannerBase: React.CSSProperties = { margin: "0 9px 16px", padding: "10px 14px", borderRadius: 8, border: "1px solid", fontSize: 12.5, lineHeight: 1.5, fontFamily: "'Inter','Mulish',sans-serif" };
  const chip = (t: string, i: number) => <span key={i} style={{ fontSize: 10.5, color: inkMid, background: chipBg, border: `1px solid ${boxBorder}`, borderRadius: 999, padding: "2px 8px" }}>{t}</span>;
  const pillBtn = (on: boolean): React.CSSProperties => ({ padding: "6px 12px", border: `1px solid ${on ? accent : (isDark ? "rgba(255,255,255,.18)" : "#cbd5e1")}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Inter','Mulish',sans-serif", background: on ? "linear-gradient(135deg,#ea580c,#f97316)" : (isDark ? "#1a2740" : "#fff"), color: on ? "#fff" : inkMid });

  const result: NicheAdsResult | null = run?.result ?? null;
  const countryNames = countries.length ? countries.map(c => c.name) : [DEFAULT_COUNTRY];
  const cpcFor = (kw: string) => result?.keywords.find(k => k.keyword.toLowerCase() === kw.toLowerCase())?.cpc;
  const sortedDomains = result ? [...result.domains].sort((a, b) =>
    sortKey === "keywords" ? b.keywords.length - a.keywords.length : sortKey === "cities" ? b.cities.length - a.cities.length : b.appearances - a.appearances) : [];

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      {balance?.configured && balance.ok === false && <div style={{ ...bannerBase, background: isDark ? "rgba(239,68,68,.12)" : "#fef2f2", borderColor: isDark ? "rgba(239,68,68,.4)" : "#fecaca", color: isDark ? "#fca5a5" : "#991b1b" }}><strong>DataForSEO account issue:</strong> {balance.error || "account unreachable"}.</div>}
      {balance?.configured && balance.ok && balance.balanceUsd != null && balance.balanceUsd < LOW_BALANCE_USD && <div style={{ ...bannerBase, background: isDark ? "rgba(245,158,11,.12)" : "#fffbeb", borderColor: isDark ? "rgba(245,158,11,.4)" : "#fcd34d", color: isDark ? "#fcd34d" : "#92400e" }}><strong>Low DataForSEO balance: {usd(balance.balanceUsd)}.</strong> Top up at <a href="https://app.dataforseo.com/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700 }}>dataforseo.com</a>.</div>}

      <div style={{ padding: "0 9px", marginBottom: 16 }}>
        <div className="cd-hd-brand" style={brandTitle}>Niche Ads Scraper</div>
        <div className="cd-help" style={help}>
          Find every domain running Google ads for your keywords across the cities you choose. Each search
          <strong> screenshots the live results page</strong> for that city and reads every sponsored result off the
          image with AI — so it catches search ads, <strong>Local Services / Google Screened</strong>, sponsored map
          cards, and shopping ads, then compiles a de-duplicated table of advertisers with the keywords they target and
          where they show. Ads shift by location, time of day, and device — run repeatedly (or on a schedule) to catch
          advertisers who only appear at certain times. Powered by Bright Data + Claude vision; keyword CPC comes from DataForSEO.
        </div>

        {/* ── Config ── */}
        <div style={{ border: `1px solid ${boxBorder}`, borderRadius: 8, background: boxBg, padding: "12px 14px", marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Keywords */}
          <label style={{ display: "block" }}><span style={lbl}>Keywords <span style={{ textTransform: "none", fontWeight: 400, color: inkMuted, letterSpacing: 0 }}>— one per line</span></span>
            <textarea value={keywordsText} disabled={notReady} onChange={e => setKeywordsText(e.target.value)} onBlur={() => void persist()} rows={4}
              placeholder={"e.g. emergency plumber\n24 hour locksmith\nwater heater repair"} style={{ ...inp, resize: "vertical", fontFamily: "'Roboto Mono',ui-monospace,monospace" }} /></label>

          {/* Cities */}
          <div>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ flex: "0 0 auto", minWidth: 150 }}><span style={lbl}>Country</span>
                <select value={country} disabled={notReady} onChange={e => { setCountry(e.target.value); setTimeout(() => void persist(), 0); }} style={{ ...inp, cursor: "pointer" }}>
                  {countryNames.includes(country) ? null : <option value={country}>{country}</option>}
                  {countryNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select></label>
              <label style={{ flex: "1 1 220px", minWidth: 180 }}><span style={lbl}>Add a city</span>
                <CityPicker country={country} value={cityPick} onChange={setCityPick} isDark={isDark} disabled={notReady} inpStyle={inp} /></label>
              <button onClick={addCity} disabled={notReady || !cityPick} style={{ ...pillBtn(false), height: 34, opacity: !cityPick ? 0.5 : 1 }}>+ Add city</button>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 }}>
              <label style={{ flex: "1 1 200px", minWidth: 160 }}><span style={lbl}>Or a radius around a city</span>
                <input value={radiusCenter} disabled={notReady} onChange={e => setRadiusCenter(e.target.value)} placeholder="e.g. Orlando, FL" style={inp} /></label>
              <label style={{ flex: "0 0 110px" }}><span style={lbl}>Miles</span>
                <input type="number" min={1} max={200} value={radiusMiles} disabled={notReady} onChange={e => setRadiusMiles(Math.max(1, Math.min(200, +e.target.value || 1)))} style={inp} /></label>
              <button onClick={() => void populateRadius()} disabled={notReady || radiusBusy || !radiusCenter.trim()} style={{ ...pillBtn(false), height: 34, opacity: radiusBusy || !radiusCenter.trim() ? 0.5 : 1 }}>{radiusBusy ? "Finding…" : "Populate cities"}</button>
            </div>
            {cities.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                {cities.map((c, i) => (
                  <span key={`${c.label}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: inkMid, background: chipBg, border: `1px solid ${boxBorder}`, borderRadius: 999, padding: "3px 6px 3px 10px" }}>
                    {c.label}
                    <button onClick={() => removeCity(i)} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: inkMuted, padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
                  </span>
                ))}
                <button onClick={() => { setCities([]); setTimeout(() => void persist(), 0); }} style={{ background: "none", border: "none", cursor: "pointer", color: link, fontSize: 11.5, fontWeight: 700 }}>Clear all</button>
              </div>
            )}
          </div>

          {/* Devices + surfaces */}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div><span style={lbl}>Device</span>
              <div style={{ display: "flex", gap: 6 }}>
                {["desktop", "mobile"].map(d => <button key={d} disabled={notReady} onClick={() => toggle(devices, d, setDevices)} style={pillBtn(devices.includes(d))}>{d[0].toUpperCase() + d.slice(1)}</button>)}
              </div>
              <span style={{ fontSize: 10.5, color: inkMuted }}>Both doubles the searches.</span>
            </div>
            <div><span style={lbl}>Ad types</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {SURFACES.map(([v, label]) => <button key={v} disabled={notReady} onClick={() => toggle(surfaces, v, setSurfaces)} style={pillBtn(surfaces.includes(v))}>{label}</button>)}
              </div>
            </div>
            <div><span style={lbl}>Pages to scan</span>
              <div style={{ display: "flex", gap: 6 }}>
                {[1, 2, 3].map(p => <button key={p} disabled={notReady} onClick={() => { setPages(p); setTimeout(() => void persist(), 0); }} style={pillBtn(pages === p)}>{p}</button>)}
              </div>
              <span style={{ fontSize: 10.5, color: inkMuted }}>More pages catch bottom-of-page ads (each page = 1 request).</span>
            </div>
          </div>

          {/* Schedule */}
          <div style={{ borderTop: `1px solid ${boxBorder}`, paddingTop: 12 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: schedEnabled ? 10 : 0 }}>
              <input type="checkbox" checked={schedEnabled} disabled={notReady} onChange={e => { setSchedEnabled(e.target.checked); setTimeout(() => void persist(), 0); }} />
              <span style={{ ...lbl, marginBottom: 0 }}>Run on a schedule</span>
            </label>
            {schedEnabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div><span style={{ fontSize: 11, color: inkMuted }}>Days</span>
                  <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>{DOW.map((d, i) => <button key={i} onClick={() => toggleDay(i)} style={{ ...pillBtn(schedDays.includes(i)), padding: "5px 9px" }}>{d}</button>)}</div>
                </div>
                <div><span style={{ fontSize: 11, color: inkMuted }}>Times of day ({schedTz})</span>
                  <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                    {TIME_PRESETS.map(([label, t]) => <button key={t} onClick={() => toggleTime(t)} style={{ ...pillBtn(schedTimes.includes(t)), padding: "5px 9px" }}>{label} {t}</button>)}
                    {schedTimes.filter(t => !TIME_PRESETS.some(p => p[1] === t)).map(t => <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, ...pillBtn(true), padding: "5px 9px" }}>{t}<button onClick={() => toggleTime(t)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 0, fontSize: 13 }}>×</button></span>)}
                    <input type="time" value={customTime} onChange={e => setCustomTime(e.target.value)} style={{ ...inp, width: 120 }} />
                    <button onClick={addCustomTime} style={{ ...pillBtn(false), padding: "5px 9px" }}>Add time</button>
                  </div>
                </div>
                {nextRunAt && <span style={{ fontSize: 11.5, color: ink2 }}>Next scheduled run: <strong style={{ color: ink }}>{fmtDateTime(nextRunAt)}</strong></span>}
              </div>
            )}
          </div>

          {/* Estimate + Run */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", borderTop: `1px solid ${boxBorder}`, paddingTop: 12 }}>
            <button onClick={() => void doRun()} disabled={running || notReady}
              style={{ padding: "10px 18px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", cursor: running || notReady ? "default" : "pointer", background: running || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap" }}>
              {running ? "Scanning…" : "Run now"}
            </button>
            <span style={{ fontSize: 12.5, color: overCap ? red : ink2 }}>
              ≈ <strong>{searches}</strong> search{searches === 1 ? "" : "es"}{pages > 1 ? ` × ${pages} pages = ${requests} requests` : ""} · ~{usd(requests * PER_SEARCH_USD)}{overCap ? ` · over the ${MAX_SEARCHES} cap` : ""}
            </span>
            {running && <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: ink2 }}><span className="lsg-spinner" />{status?.text}</span>}
            {notReady && <span style={{ fontSize: 12.5, fontWeight: 600, color: link }}>Select a website to use this tool.</span>}
          </div>
        </div>
      </div>

      {status && !running && <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 12.5, color: status.tone === "err" ? red : ink2 }}>{status.text}</div>}

      {/* Progress bar while running */}
      {running && run && !isTerminal(run.status) && run.total > 0 && (
        <div style={{ padding: "0 9px", marginBottom: 16 }}>
          <div style={{ height: 8, background: boxBg, borderRadius: 4, overflow: "hidden", border: `1px solid ${boxBorder}` }}>
            <div style={{ width: `${Math.min(100, Math.round((run.done / run.total) * 100))}%`, height: "100%", background: "linear-gradient(90deg,#ea580c,#f97316)", transition: "width .4s" }} />
          </div>
        </div>
      )}

      {!result ? (
        <div style={{ padding: "0 9px" }}><p className="cd-help">{!loaded ? "Loading…" : running ? "Scanning… advertisers will appear here as they're found." : "No scrape yet — add keywords and cities, then Run now."}</p></div>
      ) : (
        <>
          {/* Run meta + history */}
          <div style={{ padding: "0 9px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 11.5, color: inkMuted }}>{run && fmtDateTime(run.createdAt)} · {run?.trigger === "scheduled" ? "scheduled" : "manual"} · {result.stats.searches} searches · {result.domains.length} domains · {result.stats.adsFound} ad placements</span>
            {runs.length > 0 && (
              <select value={selectedRun} onChange={e => void onSelectRun(e.target.value)} style={{ ...inp, width: "auto", padding: "5px 9px", cursor: "pointer" }}>
                <option value="latest">Latest run</option>
                {runs.map(r => <option key={r.id} value={r.id}>{fmtDateTime(r.createdAt)} · {r.status}</option>)}
              </select>
            )}
          </div>

          {/* Keywords panel (Volume + CPC) */}
          {result.keywords.length > 0 && (
            <div style={{ padding: "0 9px", marginBottom: 24 }}>
              <div className="cd-section">
                <div className="cd-section-hd"><span className="cd-hd-brand">Keywords</span></div>
                <div style={{ overflowX: "auto" }}>
                  <table className="cd-history" style={{ margin: 0, width: "100%" }}>
                    <thead><tr><th style={{ textAlign: "left" }}>Keyword</th><th style={{ textAlign: "right" }}>Search Volume</th><th style={{ textAlign: "right" }}>CPC</th></tr></thead>
                    <tbody>
                      {result.keywords.map(k => (
                        <tr key={k.keyword}>
                          <td style={{ fontSize: 12, color: inkMid }}>{k.keyword}</td>
                          <td style={{ fontSize: 12, color: ink2, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{k.volume != null ? k.volume.toLocaleString() : "—"}</td>
                          <td style={{ fontSize: 12, color: ink, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{k.cpc != null ? usd(k.cpc) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Domains table */}
          <div style={{ padding: "0 9px", marginBottom: 24 }}>
            <div className="cd-section">
              <div className="cd-section-hd" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <span className="cd-hd-brand">Advertisers ({result.domains.length})</span>
                <label style={{ fontSize: 11.5, color: ink2 }}>Sort by{" "}
                  <select value={sortKey} onChange={e => setSortKey(e.target.value as typeof sortKey)} style={{ ...inp, width: "auto", padding: "4px 8px", cursor: "pointer", display: "inline-block" }}>
                    <option value="appearances">Appearances</option><option value="keywords">Keywords</option><option value="cities">Cities</option>
                  </select>
                </label>
              </div>
              {result.domains.length === 0 ? (
                <div>
                  <p className="cd-help">No ads found for this grid. Try more keywords/cities, add mobile, or run at a different time of day.</p>
                  {result.debug && (
                    <div style={{ marginTop: 8, fontSize: 11.5, color: inkMuted, fontFamily: "'Roboto Mono',ui-monospace,monospace", lineHeight: 1.6 }}>
                      <div>Searches: {result.debug.tasksOk} ok · {result.debug.tasksFailed} failed</div>
                      <div>Status codes: {Object.entries(result.debug.statusCodes).map(([c, n]) => `${c}×${n}`).join(", ") || "—"}</div>
                      <div>Ad types found: {Object.entries(result.debug.itemTypes).map(([t, n]) => `${t}×${n}`).join(", ") || "none"}</div>
                      {result.debug.sampleError && <div style={{ color: red }}>First error: {result.debug.sampleError}</div>}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="cd-history" style={{ margin: 0, width: "100%" }}>
                    <thead><tr>
                      <th style={{ textAlign: "left" }}>Domain</th><th style={{ textAlign: "left" }}>Ad Types</th>
                      <th style={{ textAlign: "left" }}>Keywords</th><th style={{ textAlign: "left" }}>Cities</th>
                      <th style={{ textAlign: "left" }}>Device</th><th style={{ textAlign: "left" }}>Best Position</th>
                      <th style={{ textAlign: "right" }}># Keywords</th><th style={{ textAlign: "right" }}># Cities</th>
                      <th style={{ textAlign: "right", cursor: "help" }} title="How many times this domain's ads appeared across your grid — counted per keyword × city × device and per ad slot (a top ad and a bottom ad count as 2). It reflects reach within this run, not the number of distinct ads they run.">Placements</th>
                      <th style={{ textAlign: "left" }}>Business</th><th></th>
                    </tr></thead>
                    <tbody>
                      {sortedDomains.map(d => (
                        <tr key={d.domain || d.firmName}>
                          <td style={{ fontSize: 12 }}>{d.domain
                            ? <a href={`https://${d.domain.replace(/^https?:\/\//, "").split(/[\s/›|,]/)[0]}`} target="_blank" rel="noopener noreferrer" style={{ color: link, fontWeight: 700, wordBreak: "break-word" }}>{d.domain}</a>
                            : <span style={{ fontWeight: 700, color: ink, wordBreak: "break-word" }}>{d.firmName || "—"}<span style={{ fontSize: 9.5, color: inkMuted, marginLeft: 6, whiteSpace: "nowrap" }}>no website</span></span>}</td>
                          <td><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{d.adTypes.map((t, i) => <span key={i} style={{ fontSize: 9.5, fontWeight: 700, color: t === "search" || t === "paid" ? (isDark ? "#93c5fd" : "#2563eb") : t === "map" || t === "localpack" ? (isDark ? "#fcd34d" : "#b45309") : t === "shopping" ? (isDark ? "#c4b5fd" : "#7c3aed") : green, background: chipBg, border: `1px solid ${boxBorder}`, borderRadius: 4, padding: "1px 6px" }}>{AD_TYPE_LABEL[t] ?? t}</span>)}</div></td>
                          <td style={{ maxWidth: 260 }}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{d.keywords.slice(0, 6).map((kw, i) => <span key={i} title={cpcFor(kw) != null ? `CPC ${usd(cpcFor(kw)!)}` : undefined} style={{ fontSize: 10.5, color: inkMid, background: chipBg, border: `1px solid ${boxBorder}`, borderRadius: 999, padding: "2px 8px" }}>{kw}</span>)}{d.keywords.length > 6 && chip(`+${d.keywords.length - 6}`, 99)}</div></td>
                          <td style={{ fontSize: 11.5, color: ink2, maxWidth: 220 }}>{d.cities.slice(0, 4).join(", ")}{d.cities.length > 4 ? ` +${d.cities.length - 4}` : ""}</td>
                          <td style={{ fontSize: 11.5, color: ink2, whiteSpace: "nowrap" }}>{deviceLabel(d.devices)}</td>
                          <td style={{ fontSize: 11.5, color: inkMid, whiteSpace: "nowrap" }}>{d.position || "—"}</td>
                          <td style={{ fontSize: 12, color: ink2, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{d.keywords.length}</td>
                          <td style={{ fontSize: 12, color: ink2, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{d.cities.length}</td>
                          <td style={{ fontSize: 12, color: ink, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }} title="Total ad placements found across your grid (not distinct ads).">{d.appearances}</td>
                          <td style={{ fontSize: 11.5, color: ink2 }}>
                            {d.gbpUrl
                              ? <a href={d.gbpUrl} target="_blank" rel="noopener noreferrer" style={{ color: link, fontWeight: 700 }} title="Open Google Business Profile">{d.firmName || "View profile"} ↗</a>
                              : <a href={`https://www.google.com/maps/search/${encodeURIComponent(d.firmName || d.domain)}`} target="_blank" rel="noopener noreferrer" style={{ color: link, fontWeight: 700 }} title="Search on Google Maps to find its Business Profile">{d.firmName || "Find on Maps"} ↗</a>}
                            {d.rating != null ? <span style={{ color: inkMuted }}> · ★{d.rating}{d.reviews != null ? ` (${d.reviews})` : ""}</span> : null}
                            {d.phone ? <div style={{ fontSize: 11, color: inkMuted }}>{d.phone}</div> : null}
                          </td>
                          <td>{d.domain
                            ? <a href={transparencyUrl(d.domain, country)} target="_blank" rel="noopener noreferrer" style={{ ...pillBtn(false), padding: "4px 10px", fontSize: 11, textDecoration: "none", display: "inline-block", whiteSpace: "nowrap" }}>Transparency ↗</a>
                            : <span style={{ fontSize: 11, color: inkMuted }}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.debug && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: "pointer", fontSize: 11.5, color: inkMuted }}>Diagnostics</summary>
                  <div style={{ marginTop: 6, fontSize: 11, color: inkMuted, fontFamily: "'Roboto Mono',ui-monospace,monospace", lineHeight: 1.6 }}>
                    <div>Searches: {result.debug.tasksOk} ok · {result.debug.tasksFailed} failed</div>
                    <div>Status codes: {Object.entries(result.debug.statusCodes).map(([c, n]) => `${c}×${n}`).join(", ") || "—"}</div>
                    <div>Ad types found: {Object.entries(result.debug.itemTypes).map(([t, n]) => `${t}×${n}`).join(", ") || "none"}</div>
                    {result.debug.sampleError && <div style={{ color: red }}>First error: {result.debug.sampleError}</div>}
                  </div>
                </details>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
